import { useCallback, useEffect, useRef } from "react";

// wait after the last keystroke before a search request
export const SEARCH_DEBOUNCE_MS = 300;

/**
 * @description returns a debounced version of `callback`: it runs `delay` ms after the last call.
 * The latest callback is always used (no stale closures). Pending calls are dropped on unmount.
 * @param {*function to debounce} callback
 * @param {*wait in ms} delay
 * @returns [debounced, cancel]
 */
export default function useDebouncedCallback(callback, delay = 300) {
  const callbackRef = useRef(callback);
  const timerRef = useRef(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const cancel = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const debounced = useCallback(
    (...args) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        callbackRef.current(...args);
      }, delay);
    },
    [delay]
  );

  useEffect(() => cancel, [cancel]);

  return [debounced, cancel];
}
